import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking and monitoring
 * 
 * This should be called once at application startup (in index.js)
 * before React renders.
 */
export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "development";
  const enabled = process.env.REACT_APP_SENTRY_ENABLED !== "false"; // Default to true unless explicitly disabled

  // Don't initialize if DSN is missing or Sentry is disabled
  if (!dsn || !enabled) {
    console.log("Sentry initialization skipped:", !dsn ? "Missing DSN" : "Disabled in environment");
    return;
  }

  Sentry.init({
    dsn,
    environment,
    
    // Set sample rate for performance monitoring (0.0 to 1.0)
    // 1.0 = 100% of transactions, 0.1 = 10% of transactions
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,

    // Enable React error tracking
    integrations: [
      Sentry.browserTracingIntegration({
        // Set sampling rate for navigation/page load transactions
        tracePropagationTargets: ["localhost", /^https:\/\/.*\.playground\.ca/],
      }),
      Sentry.replayIntegration({
        // Sample rate for session replays (0.0 to 1.0)
        // Only capture replays on errors to save quota
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter out certain errors that are not actionable
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      // Random plugins/extensions
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      // Facebook related errors
      "fb_xd_fragment",
      // Network errors that are expected
      "NetworkError",
      "Network request failed",
      // Script loading errors from external domains
      /^Script error\.?$/,
      /^Javascript error: Script error\.? on line 0$/,
      // Browser extension errors
      "runtime.sendMessage",
      "Tab not found",
      /Invalid call to runtime\.sendMessage/,
      /chrome\.runtime/,
      /safari\.extension/,
    ],

    // Filter out certain URLs
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Facebook flakiness
      /graph\.facebook\.com/i,
    ],

    // Hook for filtering or modifying events before they're sent
    beforeSend(event, hint) {
      // Filter out events with no valuable information
      if (event.exception) {
        const error = hint.originalException;
        
        // Don't send errors from browser extensions
        if (error && error.stack) {
          if (
            error.stack.includes("chrome-extension://") ||
            error.stack.includes("safari-extension://") ||
            error.stack.includes("moz-extension://")
          ) {
            return null;
          }
        }

        // Filter out browser extension API errors
        const errorMessage = error?.message || event.exception.values?.[0]?.value || "";
        if (
          errorMessage.includes("runtime.sendMessage") ||
          errorMessage.includes("chrome.runtime") ||
          errorMessage.includes("safari.extension") ||
          errorMessage.includes("Tab not found")
        ) {
          return null;
        }
      }

      return event;
    },
  });

  console.log(`Sentry initialized for environment: ${environment}`);
};

/**
 * Capture a form submission error with rich context
 * 
 * @param {Error} error - The error object
 * @param {Object} context - Additional context about the form submission
 */
export const captureFormError = (error, context = {}) => {
  const {
    formId,
    sheetName,
    responseStatus,
    responseText,
    formType,
    userId,
    fieldCount,
  } = context;

  // Set custom fingerprint to group similar form errors together
  const fingerprint = ["form-submission-error", formId || "unknown", String(responseStatus || "unknown")];

  Sentry.withScope((scope) => {
    // Set context for better debugging
    scope.setContext("form", {
      formId: formId || "N/A",
      sheetName: sheetName || "N/A",
      formType: formType || "google-form",
      fieldCount: fieldCount || 0,
    });

    scope.setContext("response", {
      status: responseStatus || "N/A",
      statusText: responseText || "N/A",
    });

    // Set user context if available
    if (userId) {
      scope.setUser({ id: userId });
    }

    // Set custom fingerprint for grouping
    scope.setFingerprint(fingerprint);

    // Set level based on error type
    if (responseStatus === 404 || responseStatus === 403) {
      scope.setLevel("error");
      scope.setTag("error_type", "archived_script");
    } else if (!responseStatus) {
      scope.setLevel("warning");
      scope.setTag("error_type", "network_error");
    } else {
      scope.setLevel("error");
      scope.setTag("error_type", "http_error");
    }

    // Add additional tags
    scope.setTag("component", "DynamicGoogleFormSubmitter");
    scope.setTag("feature", "form_submission");

    // Capture the exception
    Sentry.captureException(error);
  });
};

/**
 * Add a breadcrumb for tracking user actions
 * 
 * @param {string} message - Description of the action
 * @param {Object} data - Additional data
 */
export const addBreadcrumb = (message, data = {}) => {
  Sentry.addBreadcrumb({
    message,
    level: "info",
    data,
  });
};

export default Sentry;
